import {
  type ExamType,
  isUsExamType,
  type UsExamType,
  type UsFindingValue,
  type UsSidedAnswer,
  type UsWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { otherTextKey } from "../worksheet-field-defs";
import { US_AORTA } from "./definitions/us-aorta";
import { US_APPENDIX } from "./definitions/us-appendix";
import { US_BILIARY } from "./definitions/us-biliary";
import { US_BILIARY_RENAL } from "./definitions/us-biliary-renal";
import { US_BLADDER } from "./definitions/us-bladder";
import { US_CARDIAC } from "./definitions/us-cardiac";
import { US_CARDIAC_THORACIC } from "./definitions/us-cardiac-thoracic";
import { US_DVT } from "./definitions/us-dvt";
import { US_EFAST } from "./definitions/us-efast";
import { US_OB_TRANSABDOMINAL } from "./definitions/us-ob-transabdominal";
import { US_OB_TRANSVAGINAL } from "./definitions/us-ob-transvaginal";
import { US_OCULAR } from "./definitions/us-ocular";
import { US_RENAL } from "./definitions/us-renal";
import { US_RUSH } from "./definitions/us-rush";
import { US_SBO } from "./definitions/us-sbo";
import { US_SOFT_TISSUE } from "./definitions/us-soft-tissue";
import { US_TESTICULAR } from "./definitions/us-testicular";
import { US_THORACIC } from "./definitions/us-thoracic";
import {
  sidedKey,
  type UsFieldDef,
  type UsOption,
  type UsSection,
  type UsSectionSide,
  type UsWorksheetDefinition,
} from "./us-fields";

export const US_DEFINITIONS: Partial<
  Record<UsExamType, UsWorksheetDefinition>
> = {
  us_efast: US_EFAST,
  us_cardiac: US_CARDIAC,
  us_thoracic: US_THORACIC,
  us_renal: US_RENAL,
  us_bladder: US_BLADDER,
  us_aorta: US_AORTA,
  us_ob_transabdominal: US_OB_TRANSABDOMINAL,
  us_cardiac_thoracic: US_CARDIAC_THORACIC,
  us_rush: US_RUSH,
  us_biliary: US_BILIARY,
  us_biliary_renal: US_BILIARY_RENAL,
  us_appendix: US_APPENDIX,
  us_sbo: US_SBO,
  us_ob_transvaginal: US_OB_TRANSVAGINAL,
  us_dvt: US_DVT,
  us_soft_tissue: US_SOFT_TISSUE,
  us_ocular: US_OCULAR,
  us_testicular: US_TESTICULAR,
};

export const INDICATION_OTHER_KEY = otherTextKey("indication");

export const isOfferableExamType = (examType: ExamType): boolean =>
  !isUsExamType(examType) || US_DEFINITIONS[examType] !== undefined;

export const usDefinitionFor = (
  examType: UsExamType,
): UsWorksheetDefinition | undefined => US_DEFINITIONS[examType];

export type UsSectionInstance = {
  section: UsSection;
  side: UsSectionSide | null;
  key: string;
  limitedKey: string;
  reasonKey: string | null;
  fields: readonly UsFieldDef[];
};

const withSide = (field: UsFieldDef, side: UsSectionSide): UsFieldDef => {
  const showWhen = field.showWhen && {
    ...field.showWhen,
    field: sidedKey(field.showWhen.field, side),
  };
  if (field.kind === "number" || field.kind === "text") {
    return { ...field, id: sidedKey(field.id, side), showWhen };
  }
  return {
    ...field,
    id: sidedKey(field.id, side),
    showWhen,
    options: field.options.map((option) => ({
      ...option,
      reveals: option.reveals?.map((revealed) => withSide(revealed, side)),
    })),
  };
};

const instance = (
  section: UsSection,
  side: UsSectionSide | null,
): UsSectionInstance => {
  const key = side ? `${section.id}.${side}` : section.id;
  const reason = section.technicallyLimitedReason;
  return {
    section,
    side,
    key,
    limitedKey: section.technicallyLimitedBy ?? key,
    reasonKey: reason && side ? sidedKey(reason, side) : (reason ?? null),
    fields: side
      ? section.fields.map((field) => withSide(field, side))
      : section.fields,
  };
};

export const sectionInstances = (
  definition: UsWorksheetDefinition,
): UsSectionInstance[] =>
  definition.sections.flatMap((section) =>
    section.repeatPerSide
      ? section.repeatPerSide.map((side) => instance(section, side))
      : [instance(section, null)],
  );

export const selectedValues = (value: UsFindingValue | undefined): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === "string" ? item : item.value));
  }
  return [];
};

const selectedOptions = (
  field: UsFieldDef,
  value: UsFindingValue | undefined,
): UsOption[] => {
  if (field.kind === "number" || field.kind === "text") return [];
  const values = selectedValues(value);
  return field.options.filter((option) => values.includes(option.value));
};

const isShown = (
  findings: UsWorksheetData["findings"],
  field: UsFieldDef,
): boolean => {
  if (!field.showWhen) return true;
  const values = selectedValues(findings[field.showWhen.field]);
  return values.some((value) => field.showWhen?.in.includes(value));
};

export type VisibleUsField = { field: UsFieldDef; depth: number };

export const visibleFields = (
  fields: readonly UsFieldDef[],
  findings: UsWorksheetData["findings"],
  depth = 0,
): VisibleUsField[] =>
  fields.flatMap((field) =>
    isShown(findings, field)
      ? [
          { field, depth },
          ...selectedOptions(field, findings[field.id]).flatMap((option) =>
            visibleFields(option.reveals ?? [], findings, depth + 1),
          ),
        ]
      : [],
  );

export const visibleInstances = (
  definition: UsWorksheetDefinition,
  findings: UsWorksheetData["findings"],
): UsSectionInstance[] =>
  sectionInstances(definition).filter(({ section, side }) => {
    const rule = section.showSideWhen;
    if (!rule || !side) return true;
    return selectedValues(findings[rule.field]).some((value) =>
      rule.in[side]?.includes(value),
    );
  });

export const effectiveIndication = (
  definition: UsWorksheetDefinition,
  data: UsWorksheetData,
): string[] =>
  definition.fixedIndication
    ? definition.indications.map((indication) => indication.value)
    : data.indication;

const allFields = (fields: readonly UsFieldDef[]): UsFieldDef[] =>
  fields.flatMap((field) => [
    field,
    ...(field.kind === "number" || field.kind === "text"
      ? []
      : field.options.flatMap((option) => allFields(option.reveals ?? []))),
  ]);

export const definitionFieldIds = (
  definition: UsWorksheetDefinition,
): string[] =>
  sectionInstances(definition).flatMap((instance) => [
    ...allFields(instance.fields).map((field) => field.id),
    ...(instance.reasonKey ? [instance.reasonKey] : []),
  ]);

type NormalImpressionTarget = { fieldId: string; value: string; label: string };

const normalImpressionTargets = (
  definition: UsWorksheetDefinition,
): NormalImpressionTarget[] =>
  (definition.normalImpressions ?? []).flatMap(({ sectionId, value }) =>
    sectionInstances(definition)
      .filter(({ section }) => section.id === sectionId)
      .flatMap(({ fields }) => fields)
      .flatMap((field) => {
        if (field.kind !== "multi") return [];
        const option = field.options.find(
          (candidate) => candidate.value === value,
        );
        return option
          ? [{ fieldId: field.id, value, label: option.label }]
          : [];
      })
      .slice(0, 1),
  );

const normalImpressionValues = (
  definition: UsWorksheetDefinition,
): Map<string, string[]> => {
  const byField = new Map<string, string[]>();
  for (const { fieldId, value } of normalImpressionTargets(definition)) {
    byField.set(fieldId, [...(byField.get(fieldId) ?? []), value]);
  }
  return byField;
};

export const normalizeUs = (data: UsWorksheetData): UsWorksheetData => {
  const definition = usDefinitionFor(data.examType);
  if (!definition) return data;
  const instances = visibleInstances(definition, data.findings);
  const shown = instances.flatMap((instance) =>
    visibleFields(instance.fields, data.findings),
  );
  const findings = Object.fromEntries(
    shown.flatMap(({ field }) => {
      const value = data.findings[field.id];
      const entries: [string, UsFindingValue][] = [];
      if (value !== undefined) entries.push([field.id, value]);
      const other = data.findings[otherTextKey(field.id)];
      const revealsText = selectedOptions(field, value).some(
        (option) => option.revealsText,
      );
      if (other !== undefined && revealsText) {
        entries.push([otherTextKey(field.id), other]);
      }
      return entries;
    }),
  );
  for (const { key, reasonKey } of instances) {
    const reason = reasonKey ? data.findings[reasonKey] : undefined;
    if (reasonKey && reason !== undefined && data.technicallyLimited[key]) {
      findings[reasonKey] = reason;
    }
  }
  const indicationOther = data.findings[INDICATION_OTHER_KEY];
  if (indicationOther !== undefined && data.indication.includes("other")) {
    findings[INDICATION_OTHER_KEY] = indicationOther;
  }
  return {
    ...data,
    definitionVersion: definition.version,
    indication: effectiveIndication(definition, data),
    findings,
  };
};

const isAnswered = (value: UsFindingValue | undefined): boolean =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== "";

const isSidedComplete = (value: UsFindingValue | undefined): boolean =>
  !Array.isArray(value) ||
  value.every((item) => typeof item === "string" || item.side !== null);

export const isUsComplete = (data: UsWorksheetData): boolean => {
  const definition = usDefinitionFor(data.examType);
  if (!definition || effectiveIndication(definition, data).length === 0) {
    return false;
  }
  return visibleInstances(definition, data.findings).every((instance) => {
    if (data.technicallyLimited[instance.limitedKey]) return true;
    return visibleFields(instance.fields, data.findings).every(({ field }) => {
      const value = data.findings[field.id];
      switch (field.kind) {
        case "single":
        case "dropdown":
          return isAnswered(value);
        case "multi":
          return (
            (instance.section.role === "findings" ||
              field.optional ||
              isAnswered(value)) &&
            isSidedComplete(value)
          );
        case "number":
        case "text":
          return true;
      }
    });
  });
};

const normalAnswers = (
  definition: UsWorksheetDefinition,
  findings: UsWorksheetData["findings"],
): [string, string][] =>
  visibleInstances(definition, findings).flatMap(({ fields }) =>
    fields.flatMap((field): [string, string][] =>
      (field.kind === "single" || field.kind === "dropdown") &&
      field.normal !== null &&
      !field.showWhen
        ? [[field.id, field.normal]]
        : [],
    ),
  );

export const normalImpressionLabel = (
  definition: UsWorksheetDefinition,
): string | null => {
  const labels = normalImpressionTargets(definition).map(({ label }) => label);
  return labels.length > 0 ? labels.join(" and ") : null;
};

const hasNormalImpression = (definition: UsWorksheetDefinition): boolean =>
  normalImpressionTargets(definition).length > 0 ||
  sectionInstances(definition)
    .filter(({ section }) => section.role === "impression")
    .some(({ fields }) =>
      fields.some(
        (field) =>
          (field.kind === "single" || field.kind === "dropdown") &&
          field.normal !== null,
      ),
    );

const hasNoNormalValue = (field: UsFieldDef): boolean =>
  field.kind === "number" ||
  ((field.kind === "single" || field.kind === "dropdown") &&
    field.normal === null);

export const isUsAllNormal = (data: UsWorksheetData): boolean => {
  const definition = usDefinitionFor(data.examType);
  if (!definition || !hasNormalImpression(definition)) return false;
  const normals = normalAnswers(definition, data.findings);
  const normalIds = new Set(normals.map(([id]) => id));
  const impressionIds = normalImpressionValues(definition);
  const sideFields = new Set(
    definition.sections.flatMap(({ showSideWhen }) =>
      showSideWhen ? [showSideWhen.field] : [],
    ),
  );
  const otherAnswersEmpty = visibleInstances(definition, data.findings)
    .flatMap((instance) => visibleFields(instance.fields, data.findings))
    .every(
      ({ field }) =>
        normalIds.has(field.id) ||
        impressionIds.has(field.id) ||
        sideFields.has(field.id) ||
        hasNoNormalValue(field) ||
        !isAnswered(data.findings[field.id]),
    );
  return (
    otherAnswersEmpty &&
    normals.every(([id, value]) => data.findings[id] === value) &&
    [...impressionIds].every(
      ([id, values]) =>
        [...selectedValues(data.findings[id])].sort().join() ===
        [...values].sort().join(),
    )
  );
};

const otherImpressionMultis = (
  definition: UsWorksheetDefinition,
): [string, string[]][] => {
  const impressionIds = normalImpressionValues(definition);
  return sectionInstances(definition)
    .filter(({ section }) => section.role === "impression")
    .flatMap(({ fields }) =>
      fields.flatMap((field): [string, string[]][] =>
        field.kind === "multi" && !impressionIds.has(field.id)
          ? [[field.id, []]]
          : [],
      ),
    );
};

export const toggleUsAllNormal = (data: UsWorksheetData): UsWorksheetData => {
  const definition = usDefinitionFor(data.examType);
  if (!definition || !hasNormalImpression(definition)) return data;
  const clearing = isUsAllNormal(data);
  return {
    ...data,
    technicallyLimited: clearing ? data.technicallyLimited : {},
    findings: {
      ...data.findings,
      ...(!clearing && Object.fromEntries(otherImpressionMultis(definition))),
      ...Object.fromEntries(
        normalAnswers(definition, data.findings).map(([id, value]) => [
          id,
          clearing ? null : value,
        ]),
      ),
      ...Object.fromEntries(
        [...normalImpressionValues(definition)].map(([id, values]) => [
          id,
          clearing ? [] : values,
        ]),
      ),
    },
  };
};

const SIDE_LABELS: Record<UsSidedAnswer["side"] & string, string> = {
  right: "Right",
  left: "Left",
  both: "Both",
};

export const SECTION_SIDE_LABELS: Record<UsSectionSide, string> = {
  right: "Right",
  left: "Left",
  right_eye: "Right eye",
  left_eye: "Left eye",
};

const answerLabels = (
  field: UsFieldDef,
  findings: UsWorksheetData["findings"],
): string[] => {
  const value = findings[field.id];
  if (value === null || value === undefined || value === "") return [];
  if (field.kind === "number") return [`${value} ${field.unit}`];
  if (field.kind === "text") return [String(value)];
  const other = findings[otherTextKey(field.id)];
  const items = Array.isArray(value) ? value : [String(value)];
  return items.map((item) => {
    const code = typeof item === "string" ? item : item.value;
    const option = field.options.find((candidate) => candidate.value === code);
    const label =
      option?.revealsText && typeof other === "string" && other
        ? other
        : (option?.label ?? code);
    return typeof item !== "string" && item.side
      ? `${SIDE_LABELS[item.side]} ${label}`
      : label;
  });
};

export const instanceLabel = ({ section, side }: UsSectionInstance): string =>
  side ? `${SECTION_SIDE_LABELS[side]} ${section.label}`.trim() : section.label;

export const usIndicationLabels = (data: UsWorksheetData): string[] => {
  const definition = usDefinitionFor(data.examType);
  const other = data.findings[INDICATION_OTHER_KEY];
  const indication = definition
    ? effectiveIndication(definition, data)
    : data.indication;
  return indication.map((code) => {
    if (code === "other") {
      return typeof other === "string" && other ? other : "Other";
    }
    return (
      definition?.indications.find((indication) => indication.value === code)
        ?.label ?? code
    );
  });
};

export const usViewLabels = (data: UsWorksheetData): string[] =>
  (usDefinitionFor(data.examType)?.views ?? [])
    .filter((view) => data.views[view.value])
    .map((view) => view.label);

export const summarizeUs = (
  data: UsWorksheetData,
): { abnormal: string[]; impression: string | null } => {
  const definition = usDefinitionFor(data.examType);
  if (!definition) return { abnormal: [], impression: null };
  const instances = visibleInstances(definition, data.findings);
  const abnormal = instances
    .filter(({ section }) => section.role === "findings")
    .flatMap((instance) => {
      const prefix = instance.side
        ? `${SECTION_SIDE_LABELS[instance.side]} `
        : "";
      const reason = instance.reasonKey
        ? data.findings[instance.reasonKey]
        : undefined;
      const limited = data.technicallyLimited[instance.key]
        ? [
            `${instanceLabel(instance)}: Technically limited${
              typeof reason === "string" && reason ? ` (${reason})` : ""
            }`,
          ]
        : [];
      const answers = visibleFields(instance.fields, data.findings)
        .filter(({ field }) =>
          field.kind === "single" || field.kind === "dropdown"
            ? data.findings[field.id] !== field.normal
            : true,
        )
        .flatMap(({ field }) => {
          const labels = answerLabels(field, data.findings);
          const label = field.heading
            ? `${field.heading} ${field.label.toLowerCase()}`
            : field.label;
          return labels.length > 0
            ? [`${prefix}${label}: ${labels.join(", ")}`]
            : [];
        });
      return [...limited, ...answers];
    });
  const impressionLabels = instances
    .filter(({ section }) => section.role === "impression")
    .flatMap((instance) =>
      visibleFields(instance.fields, data.findings).flatMap(({ field }) =>
        answerLabels(field, data.findings).map((label) =>
          field.kind === "single" || field.kind === "dropdown"
            ? `${field.label}: ${label}`
            : label,
        ),
      ),
    );
  return {
    abnormal,
    impression:
      impressionLabels.length > 0 ? impressionLabels.join(", ") : null,
  };
};
